<script lang="ts" setup>
import ElInputContainer from '@/forms/ElInputContainer.vue';
import { useInput, NumberValueType, showRequiredMark, withNumberElInputProps } from '@/forms/input';
import { computed, ref } from 'vue';

const props = defineProps({
  ...withNumberElInputProps(),
  isTwoDecimals: {
    type: Boolean,
    default: false,
  },
  defaultNullValueToZeroOnBlur: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits<{
  (event: 'update:modelValue', value: NumberValueType): void;
  (event: 'overlayedBtnClicked'): void;
  (event: 'update:formattedAmount', value: NumberValueType): void;
}>();

const showOverlayedIcon = ref(false);
const computedModelValue = computed(() => {
  return props.modelValue;
});

const computedValidation = computed(() => [props.validation]);

const { value, errorMessage, uuid, fieldContext } = useInput(computedModelValue, computedValidation, props.name, newValue => {
  emit('update:modelValue', newValue);
  emit('update:formattedAmount', formattedAmount.value);
});

const formattedAmount = computed<number | null>({
  get: () => {
    if (value.value === null || value.value === undefined) return null;
    if (typeof value.value === 'string' && value.value === '') return null;
    if (typeof value.value === 'string') return Number(value.value);

    if (props.isTwoDecimals) {
      return parseFloat((value.value / 100).toFixed(2));
    }

    return value.value;
  },
  set: (inputValue: number | null | string) => {
    if (inputValue === '' || inputValue === null) {
      value.value = null;
      return;
    }

    const numValue = Number(inputValue);
    if (!isNaN(numValue)) {
      if (props.isTwoDecimals) {
        value.value = Math.round(numValue * 100);
      } else {
        value.value = numValue;
      }
    } else {
      value.value = numValue;
    }
  },
});

const onBlur = (event: Event) => {
  const inputValue = Number((event.target as HTMLInputElement).value);
  fieldContext.validate();

  // if props.min and / or props.max are set, check if the value is within the range
  if (props.min && inputValue && inputValue < props.min) {
    value.value = props.min;
  }

  if (props.max && inputValue && inputValue > props.max) {
    value.value = props.max;
  }

  if (props.defaultNullValueToZeroOnBlur) {
    resetNullValueToZero();
  }
};

const resetNullValueToZero = () => {
  if (!value.value) {
    value.value = 0;
  }
};
</script>

<template>
  <ElInputContainer
    v-bind="$attrs"
    :name="uuid"
    :label="label"
    :error-message="props.errorMessage === false ? false : props.errorMessage || errorMessage"
    :hidden-error-message="hiddenErrorMessage"
    :set-required-mark="showRequiredMark([validation])"
  >
    <template #default="{ error }">
      <input
        :id="uuid"
        v-model="formattedAmount"
        :name="props.name"
        :type="props.isTwoDecimals ? 'text' : 'number'"
        :step="step"
        :min="min"
        :max="max"
        :placeholder="placeholder"
        :disabled="disabled"
        class="el-input"
        :class="{ error }"
        :data-cy="label ? `${label.trim().replaceAll(' ', '')}-number-input` : `number-input`"
        @blur="onBlur"
        @update:model-value="showOverlayedIcon = true"
      />
    </template>
  </ElInputContainer>
</template>
